/*
 * CalcVoice — speech-output helper for the /calculator tool.
 *
 * Wraps Web Speech + Piper HD voices behind one API, guarded by the shared
 * phantom-speech watchdog (public/js/speech-engine-probe.js). The caller
 * passes ready-to-speak strings — digit characters, translated operator
 * words, the formatted result — CalcVoice owns only engine selection,
 * flood control and graceful degradation.
 *
 *   CalcVoice.init({ locale, getEnabled, onUnavailable })
 *   CalcVoice.preload()              // call when the voice toggle turns on
 *   CalcVoice.speakToken(text)       // per keypress — latest key wins
 *   CalcVoice.speakResult(text)      // the result line
 *   CalcVoice.setLocale(locale)
 *   CalcVoice.cancel()
 *
 * Piper has neural voices for en/ru/de/ar/id/pt only. ko/es/fr fall back to
 * Web Speech; if the platform has no Web Speech voice either, voice output
 * is disabled for the session and onUnavailable() is called once — never an
 * error, just a quiet degrade.
 */
(function (global) {
    'use strict';

    var PIPER_LANGS = { en: 1, ru: 1, de: 1, ar: 1, id: 1, pt: 1 };
    var RATE = 1.05;

    var cfg = {
        locale: 'en',
        getEnabled: function () { return false; },
        onUnavailable: function () {}
    };
    var localePrefix = 'en';
    var engine = 'web';          // 'web' | 'piper' | 'none'
    var diagnosed = false;
    var unavailableNotified = false;
    var piperHandle = null;

    function hasWebSpeech() {
        return typeof global.speechSynthesis !== 'undefined'
            && typeof global.SpeechSynthesisUtterance !== 'undefined';
    }
    function piperEligible() {
        return !!PIPER_LANGS[localePrefix]
            && global.PiperEngine
            && typeof global.PiperEngine.isSupported === 'function'
            && global.PiperEngine.isSupported();
    }
    function probe() {
        return global.SpeechEngineProbe || null;
    }

    function init(options) {
        options = options || {};
        if (options.locale) setLocale(options.locale);
        if (typeof options.getEnabled === 'function') cfg.getEnabled = options.getEnabled;
        if (typeof options.onUnavailable === 'function') cfg.onUnavailable = options.onUnavailable;
    }

    function setLocale(locale) {
        cfg.locale = locale || 'en';
        localePrefix = String(cfg.locale).toLowerCase().split(/[-_]/)[0] || 'en';
        diagnosed = false;          // re-diagnose for the new language
        engine = 'web';
    }

    // Run once per language: pick Web Speech vs Piper, or disable voice.
    function diagnose() {
        if (diagnosed) return;
        diagnosed = true;
        var p = probe();
        if (!p) {
            engine = hasWebSpeech() ? 'web' : (piperEligible() ? 'piper' : 'none');
            if (engine === 'none') markUnavailable();
            return;
        }
        p.diagnose(localePrefix).then(applyDiagnosis).catch(function () {});
    }

    function applyDiagnosis(res) {
        if (!res) return;
        if (res.recommend === 'piper' && piperEligible()) {
            engine = 'piper';
            preloadPiper();
        } else if (res.ok || res.recommend === 'web_speech') {
            engine = 'web';
        } else if (piperEligible()) {
            engine = 'piper';
            preloadPiper();
        } else {
            engine = 'none';
            markUnavailable();
        }
    }

    function markUnavailable() {
        engine = 'none';
        if (unavailableNotified) return;
        unavailableNotified = true;
        try { cfg.onUnavailable(); } catch (_) {}
    }

    function preloadPiper() {
        if (!piperEligible() || typeof global.PiperEngine.preload !== 'function') return;
        try { global.PiperEngine.preload(localePrefix); } catch (_) {}
    }

    // Called when the voice toggle is switched on.
    function preload() {
        diagnose();
        if (engine === 'piper') preloadPiper();
    }

    function cancel() {
        if (hasWebSpeech()) { try { global.speechSynthesis.cancel(); } catch (_) {} }
        if (piperHandle) { try { piperHandle.cancel(); } catch (_) {} piperHandle = null; }
    }

    // Core: cancel whatever is mid-flight (latest input wins) then speak.
    function speak(text, kind) {
        if (!cfg.getEnabled()) return;
        if (!text && text !== 0) return;
        if (!diagnosed) diagnose();
        if (engine === 'none') return;
        cancel();

        if (engine === 'piper' && piperEligible()) {
            speakPiper(String(text));
        } else if (hasWebSpeech()) {
            speakWeb(String(text), kind);
        }
    }

    function speakWeb(text, kind) {
        var u;
        try { u = new global.SpeechSynthesisUtterance(text); }
        catch (_) { return; }
        u.lang = cfg.locale;
        u.rate = RATE;
        u.volume = 1;
        var p = probe();
        if (p) {
            var voices = p.getVoicesForLang(localePrefix);
            if (voices && voices.length) u.voice = voices[0];
        }
        var wd = null;
        if (p && typeof p.makeWatchdog === 'function') {
            wd = p.makeWatchdog({
                onStartTimeout: 1500,
                expectedDurationMs: p.estimateDurationMs(text, RATE),
                onPhantom: function () { handlePhantom(text, kind); }
            });
            u.addEventListener('start', wd.markStart);
            u.addEventListener('end', wd.disarm);
            u.addEventListener('error', wd.disarm);
        }
        try {
            global.speechSynthesis.speak(u);
            if (wd) wd.arm();
        } catch (_) { /* silent — degrade gracefully */ }
    }

    function speakPiper(text) {
        try {
            piperHandle = global.PiperEngine.speak(text, {
                lang: localePrefix,
                rate: RATE,
                volume: 1,
                onerror: function () { markUnavailable(); }
            });
        } catch (_) { markUnavailable(); }
    }

    // Phantom Web Speech engine detected mid-utterance.
    function handlePhantom(text, kind) {
        if (piperEligible()) {
            engine = 'piper';
            preloadPiper();
            speakPiper(text);          // retry the same token on Piper
        } else {
            markUnavailable();         // ko/es/fr — nothing left to fall back to
        }
    }

    var API = {
        init: init,
        setLocale: setLocale,
        preload: preload,
        speakToken: function (text) { speak(text, 'token'); },
        speakResult: function (text) { speak(text, 'result'); },
        cancel: cancel,
        _engine: function () { return engine; }
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    global.CalcVoice = API;

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
